import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import PopulationSpreadSim from './PopulationSpreadSim';

import { initializeSimulationState, simulator, statesList, linkRenderer, nodesRenderer } from './withDeathSimulator'
import { range } from 'd3-array'
import { scaleLinear } from 'd3-scale'

const handleRadius = 11
const handleOffset = 11 / Math.sqrt(2)
const handlePath = `M 0 0 L -${handleOffset} ${handleOffset} A 11 11 1 1 0 ${handleOffset} ${handleOffset} z`

const WithResurgenceControls = ({ portalId, graph, deathRates }) => {
  const svgRef = useRef()

  const [start, setStart] = useState(10)
  const [end, setEnd] = useState(40)
  const [pressed, setPressed] = useState(null)

  const inPlayHash = {
    "IMMEDIATE-FAMILY": true,
    FAMILY: false,
    "ESSENTIAL-WORK": true,
    "NON-ESSENTIAL-WORK": false,
    FRIENDS: false
  }

  const linksInPlay = (type, day) => {
    if (day >= start && day < end) {
      return inPlayHash[type]
    } else {
      return true
    }
  }

  const width = Math.min(600, window.innerWidth)
  const daysRange = [0, 180]
  const widthScale = scaleLinear()
    .domain(daysRange)
    .range([14, width - 14])

  const ticksToSkip = Math.round(daysRange[1] * 5 / width)

  linksInPlay.changeDays = [start, end]

  const triggerInteraction = (e) => {
    const mx = (e.touches) ? e.touches[0].clientX : e.clientX
    const rect = svgRef.current.getBoundingClientRect()
    const day = widthScale.invert(mx - rect.left)

    setPressed({ 
      mouseStart: day,
      prevStart: start,
      prevEnd: end
    })
  }

  const stopInteraction = () => {
    setPressed(null)
  }

  const drag = (e, callback) => {
    if (pressed) {
      const mx = (e.touches) ? e.touches[0].clientX : e.clientX
      const rect = svgRef.current.getBoundingClientRect()
    
      const day = widthScale.invert(mx - rect.left)
      callback(day)
    }
  }

  const dragRect = (e) => {
    drag(e, (day) => {
      const delta = Math.round(day - pressed.mouseStart)
      const length = pressed.prevEnd - pressed.prevStart
      const nextStart = Math.min(daysRange[1] - length, Math.max(0, pressed.prevStart + delta))

      setStart(nextStart)
      setEnd(nextStart + length)
    })
  }

  const dragAnchors = (e) => {
    drag(e, (day) => {
      const dStart = Math.abs(pressed.mouseStart - pressed.prevStart)
      const dEnd = Math.abs(pressed.mouseStart - pressed.prevEnd)

      if (dStart > 15 && dEnd > 15) { return }

      if (dStart > dEnd) {
        const delta = Math.round(day - pressed.mouseStart)
        const nextEnd = Math.min(daysRange[1], Math.max(start + 1, pressed.prevEnd + delta))

        setEnd(nextEnd)
      } else {

        const delta = Math.round(day - pressed.mouseStart)
        const nextStart = Math.min(end - 1, Math.max(0, pressed.prevStart + delta))
        setStart(nextStart)
      }
    })
  }

  return ReactDOM.createPortal(
    <PopulationSpreadSim
      width={600}
      height={550}
      simulator={simulator}
      statesList={statesList}
      initializeSimulationState={initializeSimulationState}
      linkRenderer={linkRenderer}
      nodesRenderer={nodesRenderer}
      graph={graph}
      zoom={0.75}
      controls={{
        ...deathRates,
        linksInPlay
      }}
    >
      <span className="connection-types">Social Distancing Enforcement Period:</span>
      <div className="connection-controls">
        <svg width="100%" height="88" ref={svgRef}>
          <rect 
            x={widthScale(daysRange[0])}
            y={20}
            width={widthScale(daysRange[1]) - widthScale(daysRange[0])}
            height={40}
            fill={"#f1f1f1"}
          />
          {range(Math.floor(daysRange[1] / 7) + 1)
          .filter((i) => {
            const d = i * 7
            return d < start - 2 || d > end + 2
          })
          .map((i) => {
            return <g key={i} transform={`translate(${widthScale(i * 7)},0)`}>
              <line y1="16" y2="20" stroke="#777" />
              {(i === 0 || i % ticksToSkip === 0) ?
                <text y="12" fontSize="10" textAnchor="middle">{(i === 0) ? "Day" :  null} {i * 7}</text>
              : null}
            </g>
          })}
          <text x={widthScale(start)} y="13" fill="green" fontWeight="bold" textAnchor="middle">{start}</text>
          <text x={widthScale(end)} y="13" fill="green" fontWeight="bold" textAnchor="middle">{end}</text>
          <rect 
            x={widthScale(start)}
            y={20}
            width={widthScale(end) - widthScale(start)}
            height={40}
            style={{ fill: "url(#diagonal-stripe-2)" }} 
            onTouchStart={triggerInteraction}
            onMouseDown={triggerInteraction}
            onTouchEnd={stopInteraction}
            onMouseUp={stopInteraction}
            onMouseMove={dragRect}
            onTouchMove={dragRect}
          />
          <g transform={`translate(${widthScale(start)},60)`}>
            <path d={handlePath} fill={"#888"} />
            <circle 
              cx={0}
              cy={handleRadius}
              r={22}
              opacity={0}
              fill="red"
            />
          </g>

          <g transform={`translate(${widthScale(end)},60)`}>
            <path d={handlePath} fill={"#888"} />
            <circle 
              cx={0}
              cy={handleRadius}
              r={22}
              opacity={0}
              fill="red"
            />
          </g>

          <rect 
            width={width}
            height="30"
            y="60"
            fill="red"
            opacity={0}
            onTouchStart={triggerInteraction}
            onMouseDown={triggerInteraction}
            onTouchEnd={stopInteraction}
            onMouseUp={stopInteraction}
            onMouseMove={dragAnchors}
            onTouchMove={dragAnchors}
          />
        </svg>
      </div>
    </PopulationSpreadSim>,
    document.getElementById(portalId)
  );
};

export default WithResurgenceControls;